# Terms and Conditions

*Last updated: October 24, 2026*

These Terms and Conditions ("Terms") govern your access to and use of the **kart.win** web platform ([https://kart.win](https://kart.win)) and the **Kart Win Extractor** mobile application (`com.yanicksenn.telemetry.extractor`) operated by **Yanick Senn** ("we", "us", or "our", collectively the "Services").

By signing into or using the Services, or by clicking "Accept", you agree to be bound by the current version of these Terms.

---

## 1. Scope and Acceptance

1. **Requirement to Accept**: Every authenticated user must accept the latest version of these Terms before performing any authenticated action within the Services.
2. **Version Updates**: Whenever these Terms are updated in a new release, you will be prompted to review and accept the updated Terms before continuing to use authenticated features.
3. **Privacy Policy**: Your use of the Services is also governed by our [Privacy Policy](#/privacy), which explains how we collect, store, and protect your personal and session data.

---

## 2. Account Registration and Acceptable Use

1. **Account Credentials**: You may sign in using supported third-party identity providers (such as Sign in with Apple or Google Sign-In). You are responsible for maintaining the security of your account and any connected devices or authorized applications.
2. **Acceptable Use**: You agree to use the Services only for lawful purposes and in compliance with these Terms. You must not:
   - Attempt to gain unauthorized access to other users' accounts, teams, or private data;
   - Interfere with, disrupt, or overload the Services, servers, or networks;
   - Reverse engineer, decompile, or abuse any part of the Services or their APIs; or
   - Upload malicious code, corrupted archives, or unlawful content.
3. **Account Suspension or Termination**: We reserve the right to suspend or terminate access to the Services at any time if we reasonably believe you have violated these Terms or compromised the security or integrity of the platform.

---

## 3. User Data and Content License

1. **Ownership**: You retain all ownership rights to the data, files, configurations, and content that you upload or enter into the Services ("User Content").
2. **License to Operate the Services**: By uploading or submitting User Content to the Services, you grant us a worldwide, non-exclusive, royalty-free license to host, store, process, analyze, and display that User Content solely for the purpose of providing, maintaining, and improving the Services for you and any teams or users with whom you choose to share your data.
3. **Team Sharing**: When you join a team or explicitly share data with a team within the Services, members of that team will have access to view the shared data in accordance with your sharing settings.

---

## 4. Service Availability and "As-Is" Warranty Disclaimer

1. **Provided "As-Is"**: The Services and all associated reports, visualizations, calculations, and recommendations are provided on an **"AS IS"** and **"AS AVAILABLE"** basis without warranties of any kind, whether express, implied, or statutory.
2. **No Guarantee of Availability or Accuracy**: While we strive for high reliability, we do not warrant that the Services will be uninterrupted, error-free, or completely accurate at all times, nor do we guarantee indefinite storage or backup of User Content.

---

## 5. Limitation of Liability

To the maximum extent permitted by applicable law:

1. **Exclusion of Indirect Damages**: In no event shall **Yanick Senn** be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of data, revenue, profits, or goodwill arising out of or related to your use of or inability to use the Services.
2. **Maximum Liability**: Our total cumulative liability for any claims arising out of or relating to these Terms or the Services shall not exceed the total amount paid by you (if any) to use the Services during the twelve (12) months preceding the event giving rise to the claim.

---

## 6. Governing Law and Jurisdiction

These Terms and any dispute or claim arising out of or in connection with them or the Services shall be governed by and construed in accordance with the substantive laws of **Switzerland**, excluding its conflict of law provisions. The exclusive place of jurisdiction shall be the competent courts at the domicile of the operator in Switzerland, subject to any mandatory statutory venues.

---

## 7. Contact

If you have any questions regarding these Terms and Conditions, please contact:

- **Operator**: Yanick Senn
- **Email**: [info@yanicksenn.com](mailto:info@yanicksenn.com)
- **Website**: [https://kart.win](https://kart.win)
